import { useEffect, type MutableRefObject } from 'react';
import { useFormRegistry, FORM_ROLE_ITEMS } from 'contexts/FormRegistryProvider';
import { useActionBlocker } from 'contexts/ActionBlockerProvider';
import { safeTrack, trackFormButtonClicked, trackFormSubmitSuccess, trackFormSubmitError } from 'utils/posthog/metrics';
import { getUUID } from 'utils/common';
import { FormValidationException } from 'utils/error/FormValidationException';
import type { DataSchema } from 'types';
import type { TableRef } from 'components/common/Table/Table';
import type { TableRow } from 'components/common/Table/tableFunctions';

interface UseHeliosRecordDetailFormRegistryProps {
  formNamespace: string;
  formIdentifier: string;
  templateId: string;
  recordId: string;
  dataSchema: DataSchema;
  handleOnSaveEdit: (tableRef: MutableRefObject<TableRef | null>) => Promise<void>;
  tableRef: MutableRefObject<TableRef | null>;
  isEdit: boolean;
  addedRows: TableRow<string | number, DataSchema>[];
}

export const useHeliosRecordDetailFormRegistry = ({
  formNamespace,
  formIdentifier,
  templateId,
  recordId,
  dataSchema,
  handleOnSaveEdit,
  tableRef,
  isEdit,
  addedRows,
}: UseHeliosRecordDetailFormRegistryProps) => {
  const { register: registerForm, unregister: unregisterForm } = useFormRegistry(formNamespace);
  const { registerBlockingCondition, unregisterBlockingCondition } = useActionBlocker();

  useEffect(() => {
    const id = formIdentifier ?? getUUID();

    registerForm(
      id,
      {
        data: {
          formIdentifier: id,
          recordId,
          templateId,
        },
        api: {
          trigger: async () => {
            if (!tableRef.current) return true;
            return tableRef.current.triggerForm();
          },
          getValues: () => tableRef.current?.getValues() ?? {},
          getDirtyFields: () => tableRef.current?.getFormState().dirtyFields ?? {},
          isDirty: () => !!tableRef.current?.getFormState().isDirty,
          resetSavedForm: () => tableRef.current?.resetSavedForm(),
          buildSnapshot: () => {
            const snap = tableRef.current?.buildSnapshot();
            if (!snap) return;

            return {
              ...snap,
              addedRows,
              meta: { formIdentifier: id, recordId, templateId },
            };
          },
        },
        dataSchema,
      },
      FORM_ROLE_ITEMS
    );
    return () => unregisterForm(id);
  }, [recordId, dataSchema, registerForm, unregisterForm, templateId, addedRows, formIdentifier]);

  useEffect(() => {
    const id = `dirtyForm:${formIdentifier}`;
    registerBlockingCondition('dirtyForm', {
      id,
      isBlocking: () => !!tableRef.current?.getFormState().isDirty,
      unblock: async () => {
        if (isEdit) return Promise.resolve();

        safeTrack(trackFormButtonClicked, {
          button: 'save_rows',
          process: 'record',
          context: 'save_before_leaving',
          area: 'save_before_leaving_modal',
        });

        try {
          await handleOnSaveEdit(tableRef);
          safeTrack(trackFormSubmitSuccess, { form: 'inline_save', process: 'record', step: 'save_rows_success', area: 'save_before_leaving_modal' });
        } catch (e) {
          if (!(e instanceof FormValidationException)) {
            safeTrack(trackFormSubmitError, {
              form: 'inline_save',
              process: 'record',
              step: 'save_rows_error',
              error: 'inline_error',
              area: 'save_before_leaving_modal',
            });
          }
          throw e;
        }
      },
    });
    return () => unregisterBlockingCondition(id);
  }, [formIdentifier, handleOnSaveEdit, isEdit, registerBlockingCondition, templateId, unregisterBlockingCondition]);

  useEffect(() => {
    const id = `validForm:${formIdentifier}`;
    registerBlockingCondition('validForm', {
      id,
      isBlocking: () => !tableRef.current?.getFormState().isValid,
    });
    return () => unregisterBlockingCondition(id);
  }, [templateId, handleOnSaveEdit, registerBlockingCondition, unregisterBlockingCondition, formIdentifier]);
};
